import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { CartProduct } from "../Models/cart-product";
import { AccountService } from "./account.service";
import { CartProductService } from "./cart-product.service";
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  pluck,
  skip,
  switchMap,
} from "rxjs/operators";
import { LanguageService } from "./language.service";
import { ProductConfiguration } from "../Models/product-configuration";
import { Response } from "../Models/response";
import { DELIVERY_ADDRESS } from "../Pages/cart/delivery/delivery.component";
import { Client } from "../Models/client";

@Injectable({
  providedIn: "root",
})
export class CartService {
  private client?: Client;
  q: number = 0;
  public readonly _cartSubject = new BehaviorSubject<CartProduct[]>([]);
  public readonly cart = this._cartSubject.asObservable();
  public readonly _qn = new BehaviorSubject<number | null>(null);
  public readonly qn = this._qn.asObservable();

  //true while the client cart products are being loaded (initial load or sync after login)
  private readonly _isLoadingSubject = new BehaviorSubject<boolean>(false);
  public readonly isLoading$ = this._isLoadingSubject.asObservable();
  //number of loading requests in progress, the loading ends when all of them are done
  private pendingLoads = 0;

  public readonly _deliveryAddressSubject =
    new BehaviorSubject<DELIVERY_ADDRESS>({
      adr: null,
      mode: null,
      price: null,
    });

  public readonly _localDeliveryAddressSubject =
    new BehaviorSubject<DELIVERY_ADDRESS>({
      adr: null,
      mode: null,
      price: null,
    });

  public readonly deliveryAddress$ =
    this._deliveryAddressSubject.asObservable();
  public readonly localDeliveryAddress$ =
    this._localDeliveryAddressSubject.asObservable();

  constructor(
    private cartProductService: CartProductService,
    private accountService: AccountService,
    private languageService: LanguageService,
  ) {
    this.accountService.accountSubject
      .pipe(
        switchMap((client) => {
          if (!client) {
            return of(null); // ⬅️ IMPORTANT
          }

          this.client = client;
          this.setLoading(true);
          return this.cartProductService.getCurrentClient().pipe(
            map((body) => body || []),
            finalize(() => this.setLoading(false)),
          );
        }),
      )
      .subscribe((cart) => {
        if (cart !== null) {
          this._cartSubject.next(cart);
        }
      });

    // the products of the cart come in the current language: reload them when it changes
    this.languageService.language$
      .pipe(
        skip(1),
        filter(() => !!this.accountService.accountSubject.value),
        switchMap(() =>
          this.cartProductService
            .getCurrentClient()
            .pipe(catchError(() => of(null))),
        ),
      )
      .subscribe((cart) => {
        if (cart) {
          this._cartSubject.next(cart);
        }
      });
  }
  public readonly cartCount$ = this.cart.pipe(
    map((cart) => cart.reduce((sum, item) => sum + (item.quantity ?? 0), 0)),
  );
  public add(
    productConfiguration: ProductConfiguration,
    quantity: number,
  ): Observable<{ cartProduct: CartProduct; quantityCapped: boolean }> {
    const currentCart = this._cartSubject.value;
    const existing = currentCart.find(
      (cp) => cp.configuration?.id === productConfiguration.id,
    );

    if (existing?.id) {
      const currentQuantity = existing.quantity ?? 0;
      const maxQuantity = existing.configuration?.quantity ?? 0;

      const newQuantity = Math.min(currentQuantity + quantity, maxQuantity);

      const updatedItem: CartProduct = {
        ...existing,
        quantity: newQuantity,
        configuration: { ...existing.configuration! },
      };

      const quantityCapped = currentQuantity + quantity > maxQuantity;

      return this.update(updatedItem).pipe(
        map((cartProduct) => ({
          cartProduct,
          quantityCapped,
        })),
      );
    }

    const newItem: CartProduct = {
      cartId: this.client!.cartId,
      configurationId: productConfiguration.id,
      quantity,
      configuration: { ...productConfiguration },
    };

    return this.save(newItem).pipe(
      map((cartProduct) => ({
        cartProduct,
        quantityCapped: false,
      })),
    );
  }

  private save(cartProduct: CartProduct): Observable<CartProduct> {
    return this.cartProductService.add(cartProduct).pipe(
      map((response: any) => {
        // API returns only id
        const newCartItem: CartProduct = {
          id: response,
          cartId: cartProduct.cartId,
          configurationId: cartProduct.configurationId,
          quantity: cartProduct.quantity,
          configuration: cartProduct.configuration, // preserved from input
        };

        // Always emit a NEW array reference
        const newCart = [...this._cartSubject.value, newCartItem];
        this._cartSubject.next(newCart);

        return newCartItem;
      }),
    );
  }

  public update(cartProduct: CartProduct): Observable<CartProduct> {
    const cap = {
      id: cartProduct.id,
      cartId: cartProduct.cartId,
      configurationId: cartProduct.configuration?.id,
      quantity: cartProduct.quantity,
    };
    return this.cartProductService.update(cap).pipe(
      map((response: CartProduct) => {
        const cart = this._cartSubject.value.map((cp) =>
          cp.id === cartProduct.id ? cartProduct : cp,
        );
        this._cartSubject.next(cart);
        return response;
      }),
    );
  }

  public remove(cartProductId: number): Observable<CartProduct> {
    return this.cartProductService.delete(cartProductId).pipe(
      map((response) => {
        const cart = this._cartSubject.value.filter(
          (cartProduct: CartProduct) => cartProduct.id !== cartProductId,
        );
        if (cart.length > 0) {
          this._cartSubject.next(cart);
          return response;
        } else {
          this._cartSubject.next([]);
          return response;
        }
      }),
    );
  }

  public removeAll(): Observable<void> {
    return this.cartProductService.emptyCart().pipe(
      map((response: void) => {
        this._cartSubject.next([]);
        return response;
      }),
    );
  }

  get cartSubject(): BehaviorSubject<CartProduct[]> {
    return this._cartSubject;
  }

  /**
   * @description start (true) or end (false) a cart loading request
   * isLoading$ stays true until all the started requests are done
   */
  public setLoading(isLoading: boolean): void {
    this.pendingLoads = Math.max(this.pendingLoads + (isLoading ? 1 : -1), 0);
    this._isLoadingSubject.next(this.pendingLoads > 0);
  }
}
